import React, { useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DoneIcon from "@mui/icons-material/Done";
import { Box, Button, IconButton, MenuItem, Paper, TextField, styled, useTheme } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid";

import { FilterItem } from "../JobMultiFilterButton";
import { generateUniqueNumber } from "./FiltersHelper";

const MultiFilterPanel: React.FC<{
  initialFilters: FilterItem[];
  onApplyFilters: (filters: FilterItem[]) => void;
  onClearFilters: () => void;
}> = ({ initialFilters, onApplyFilters, onClearFilters, ...props }) => {
  const theme = useTheme();

  const PanelFooter = styled(Box)(() => ({
    display: "flex",
    flexWrap: "wrap", // Enable wrapping for smaller screens
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8, // Add space between the panel and the footer
    [theme.breakpoints.only("xs")]: {
      flexDirection: "column", // Stack buttons on smaller screens
      alignItems: "stretch", // Ensure full width
    },
  }));

  const [isMultiFilterValid, setIsMultiFilterValid] = useState(false);
  const gridApiRef = useGridApiContext();
  const columns = gridApiRef.current.getAllColumns().filter((x) => x.filterable);
  const createDefaultFilter = () => {
    return { id: generateUniqueNumber(), field: "", operator: "equals" } as FilterItem;
  };
  const [filters, setFilters] = useState<FilterItem[]>(
    initialFilters.length > 0 ? initialFilters : [createDefaultFilter()],
  );
  const lastInputRef = useRef<HTMLInputElement>(null);

  const findColumn = (field: string) => columns.find((col) => col.field === field)!;

  const generateColumnMenuItems = (currentField: string) => {
    return columns
      .filter((col) => col.field === currentField || !isFieldSelected(col.field))
      .map((col) => (
        <MenuItem key={col.field} value={col.field}>
          {col.headerName}
        </MenuItem>
      ));
  };

  const handleAddFilter = () => {
    setFilters((prev) => [...prev, createDefaultFilter()]);
  };

  const handleClearFilters = () => {
    setFilters([createDefaultFilter()]);
    onClearFilters();
  };

  const handleDeleteFilter = (id: number) => {
    setFilters((prev) => prev.filter((filter) => filter.id !== id));
  };

  const handleFieldChange = (id: number, field: keyof FilterItem, newFieldName: string) => {
    setFilters((prev) =>
      prev.map((filter) =>
        filter.id === id
          ? {
              ...filter,
              [field]: newFieldName,
              operator: findColumn(newFieldName).filterOperators![0].value,
              value: undefined,
              isValid: false,
            }
          : filter,
      ),
    );
  };

  const handleValueChange = (id: number, field: keyof FilterItem, newValueObj: any) => {
    setFilters((prev) =>
      prev.map((filter) =>
        filter.id === id ? { ...filter, [field]: newValueObj.value, isValid: newValueObj.isValid } : filter,
      ),
    );
  };

  const isFieldSelected = (field: string): boolean => {
    return filters.some((filter) => filter.field === field);
  };

  const areAllColumnsSelected = (): boolean => {
    return columns.length === filters.length;
  };

  useEffect(() => {
    const isValid = filters.every((filter) => filter.isValid);
    setIsMultiFilterValid(isValid);
  }, [filters]);

  return (
    <Paper style={{ padding: 8, boxShadow: "none" }} {...props}>
      {filters.map((filter, i) => {
        const column = columns.find((col) => col.field === filter.field);
        const customerInput = column?.filterOperators?.[0].InputComponent;

        return (
          <Box
            key={filter.id}
            sx={{ display: "flex", flexWrap: "wrap", marginTop: 1, gap: { xs: 1, sm: 0 } }}
          >
            <IconButton
              size="small"
              sx={{ marginTop: { xs: 0, sm: 1.5 } }}
              disabled={filters.length === 1}
              onClick={() => handleDeleteFilter(filter.id)}
            >
              <CloseIcon />
            </IconButton>
            <TextField
              select
              id={`multi-filter-column-${i}`}
              variant="standard"
              label="Column"
              value={filter.field}
              sx={{
                width: { xs: "100%", sm: 150 }, // Full width on smaller screens
              }}
              onChange={(e) => handleFieldChange(filter.id, "field", e.target.value)}
            >
              {generateColumnMenuItems(filter.field)}
            </TextField>
            {customerInput
              ? React.createElement(customerInput, {
                  item: filter,
                  applyValue: (value: any) => handleValueChange(filter.id, "value", value),
                  focusElementRef: i === filters.length - 1 ? lastInputRef : null,
                  autoFocus: i === filters.length - 1,
                })
              : null}
          </Box>
        );
      })}
      <PanelFooter>
        <Button
          variant="text"
          startIcon={<AddIcon />}
          disabled={areAllColumnsSelected()}
          onClick={handleAddFilter}
          sx={{
            order: 1, // Always keep this button first
            width: { xs: "100%", sm: "auto" }, // Full width on smaller screens
          }}
        >
          Add Filter
        </Button>
        <Button
          variant="text"
          startIcon={<DeleteForeverIcon />}
          onClick={handleClearFilters}
          sx={{
            order: 2,
            marginLeft: { xs: "unset", sm: "auto" }, // Push to the right on larger screens
            width: { xs: "100%", sm: "auto" }, // Full width on smaller screens
          }}
        >
          Clear Filters
        </Button>
        <Button
          variant="text"
          startIcon={<DoneIcon />}
          disabled={!isMultiFilterValid}
          onClick={() => onApplyFilters(filters)}
          sx={{
            order: 3,
            width: { xs: "100%", sm: "auto" }, // Full width on smaller screens
          }}
        >
          Apply Filters
        </Button>
      </PanelFooter>
    </Paper>
  );
};

export default MultiFilterPanel;
